import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bell, X, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";
import client from "../lib/api";

export default function NotificationBell() {
    const [open, setOpen] = useState(false);
    const [list, setList] = useState([]);
    const navigate = useNavigate();
    const unread = list.filter((n) => !n.read).length;

    const load = () => client.get("/notifications/me").then((r) => setList(r.data));

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);
        return () => clearInterval(t);
    }, []);

    const handleOpen = async () => {
        setOpen(true);
        await client.post("/notifications/read-all").catch(() => {});
        setTimeout(load, 400);
    };

    const tap = (n) => {
        setOpen(false);
        if (n.link) navigate(n.link);
    };

    const colorFor = (t) =>
        t === "success" ? "#39FF14" : t === "error" ? "#FF2424" : t === "warning" ? "#FF3B30" : "#A3A3A3";

    return (
        <>
            <button
                onClick={handleOpen}
                data-testid="notification-bell"
                className="relative w-10 h-10 surface-2 flex items-center justify-center hover:border-neon-green transition-colors"
            >
                <Bell size={18} className="text-zinc-300" />
                {unread > 0 && (
                    <span
                        data-testid="notification-badge"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full bg-neon-orange text-white text-[10px] font-bold flex items-center justify-center px-1"
                        style={{ boxShadow: "0 0 8px #FF3B30" }}
                    >
                        {unread > 9 ? "9+" : unread}
                    </span>
                )}
            </button>

            <AnimatePresence>
                {open && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setOpen(false)}
                            className="fixed inset-0 bg-black/70 z-50"
                        />
                        <motion.div
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", damping: 28, stiffness: 260 }}
                            className="fixed top-0 right-0 h-full w-full max-w-[420px] z-[60] bg-charcoal-800 border-l border-charcoal-500 flex flex-col"
                            data-testid="notification-drawer"
                        >
                            <div className="p-4 flex items-center justify-between border-b border-charcoal-500">
                                <div>
                                    <span className="label-cap text-xs text-neon-green">Notifications</span>
                                    <div className="font-display font-black text-2xl uppercase">Inbox</div>
                                </div>
                                <button
                                    onClick={() => setOpen(false)}
                                    className="w-9 h-9 surface-2 flex items-center justify-center hover:border-neon-orange"
                                    data-testid="notification-close"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                            <div className="flex-1 overflow-y-auto p-3 space-y-2">
                                {list.length === 0 ? (
                                    <div className="surface-1 p-8 text-center text-zinc-400">
                                        <CheckCheck size={28} className="mx-auto text-zinc-600 mb-2" />
                                        Nothing here yet. Train, submit, get notified.
                                    </div>
                                ) : (
                                    list.map((n) => (
                                        <button
                                            key={n.id}
                                            onClick={() => tap(n)}
                                            data-testid={`notif-${n.id}`}
                                            className={`w-full text-left surface-1 p-3 transition-all hover:border-neon-green ${
                                                !n.read ? "border-l-2" : ""
                                            }`}
                                            style={!n.read ? { borderLeftColor: colorFor(n.type) } : {}}
                                        >
                                            <div className="flex items-start gap-2">
                                                <div
                                                    className="w-2 h-2 rounded-full mt-2 shrink-0"
                                                    style={{ background: colorFor(n.type), boxShadow: `0 0 8px ${colorFor(n.type)}` }}
                                                />
                                                <div className="flex-1 min-w-0">
                                                    <div className="font-display font-bold uppercase text-sm leading-tight">
                                                        {n.title}
                                                    </div>
                                                    <div className="text-xs text-zinc-400 mt-1">{n.message}</div>
                                                    <div className="text-[10px] text-zinc-600 mt-1 label-cap">
                                                        {new Date(n.created_at).toLocaleString()}
                                                    </div>
                                                </div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
}
